import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { uploadEditorFile } from './api';
import { useTranslations } from './contexts/TranslationContext';

function ToolbarButton({ onClick, isActive, disabled, title, children }) {
  var className = 'rich-text-editor__btn' + (isActive ? ' rich-text-editor__btn--active' : '');
  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

function Toolbar({ editor, onImageUpload, isUploading }) {
  if (!editor) return null;

  return (
    <div className="rich-text-editor__toolbar">
      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleBold().run(); }}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <i className="zmdi zmdi-format-bold" />
      </ToolbarButton>

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleItalic().run(); }}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <i className="zmdi zmdi-format-italic" />
      </ToolbarButton>

      <div className="rich-text-editor__divider" />

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        H2
      </ToolbarButton>

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleHeading({ level: 3 }).run(); }}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        H3
      </ToolbarButton>

      <div className="rich-text-editor__divider" />

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleBlockquote().run(); }}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <i className="zmdi zmdi-format-quote" />
      </ToolbarButton>

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleCodeBlock().run(); }}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <i className="zmdi zmdi-code" />
      </ToolbarButton>

      <div className="rich-text-editor__divider" />

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleBulletList().run(); }}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <i className="zmdi zmdi-format-list-bulleted" />
      </ToolbarButton>

      <ToolbarButton
        onClick={function() { editor.chain().focus().toggleOrderedList().run(); }}
        isActive={editor.isActive('orderedList')}
        title="Ordered List"
      >
        <i className="zmdi zmdi-format-list-numbered" />
      </ToolbarButton>

      <div className="rich-text-editor__divider" />

      <ToolbarButton
        onClick={function() {
          var url = window.prompt('Enter URL');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        isActive={editor.isActive('link')}
        title="Link"
      >
        <i className="zmdi zmdi-link" />
      </ToolbarButton>

      <ToolbarButton onClick={onImageUpload} disabled={isUploading} title="Image">
        {isUploading
          ? <span className="spinner-border spinner-border-sm" />
          : <i className="zmdi zmdi-image" />
        }
      </ToolbarButton>
    </div>
  );
}

export default function RichTextEditor({ content, onChange, placeholder, csrfToken }) {
  var t = useTranslations();
  var fileInputRef = useRef(null);
  var editorRef = useRef(null);
  var uploadingState = useState(false);
  var isUploading = uploadingState[0];
  var setIsUploading = uploadingState[1];
  var uploadErrorState = useState(null);
  var uploadError = uploadErrorState[0];
  var setUploadError = uploadErrorState[1];

  var handleUpload = useCallback(function(file) {
    if (!file || !file.type.startsWith('image/')) return;
    var ed = editorRef.current;
    if (!ed) return;

    setIsUploading(true);
    setUploadError(null);

    uploadEditorFile(csrfToken, file)
      .then(function(result) {
        if (result.data && result.data.url) {
          ed.chain().focus().setImage({ src: result.data.url }).run();
          setUploadError(null);
        } else {
          var errorDetails = result.data && result.data.errors ? JSON.stringify(result.data.errors) : null;
          if (errorDetails) {
            setUploadError((t.image_upload_failed_with_details || t.image_upload_failed || 'Image upload failed') + ': ' + errorDetails);
          } else {
            setUploadError(t.image_upload_failed_unknown || t.image_upload_failed || 'Image upload failed: Unknown error');
          }
        }
      })
      .catch(function(err) {
        setUploadError(t.image_upload_failed_try_again || t.image_upload_failed || 'Image upload failed. Please try again.');
      })
      .finally(function() {
        setIsUploading(false);
      });
  }, [csrfToken, setUploadError, t]);

  var editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] }
      }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank'
        }
      }),
      Placeholder.configure({ placeholder: placeholder || t.editor_placeholder || 'Start writing...' })
    ],
    content: content || '',
    onUpdate: function(props) {
      onChange(props.editor.getHTML());
    },
    onCreate: function(props) {
      editorRef.current = props.editor;
    },
    editorProps: {
      handleDrop: function(_view, event) {
        var files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length > 0) {
          var file = files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: function(_view, event) {
        var items = event.clipboardData && event.clipboardData.items;
        if (!items) return false;

        for (var i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
            event.preventDefault();
            var file = items[i].getAsFile();
            if (file) handleUpload(file);
            return true;
          }
        }
        return false;
      }
    }
  });

  // Sync editor content when prop changes externally (e.g. store reset on Turbolinks navigation)
  useEffect(function() {
    if (!editor) return;
    var currentHtml = editor.getHTML();
    var newContent = content || '';
    // Only reset if content actually changed externally (not from typing)
    if (newContent !== currentHtml && (newContent === '' || newContent !== currentHtml)) {
      editor.commands.setContent(newContent, false);
    }
  }, [editor, content]);

  var handleImageButtonClick = useCallback(function() {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  var handleFileChange = useCallback(function(e) {
    var file = e.target.files && e.target.files[0];
    if (file) handleUpload(file);
    e.target.value = '';
  }, [handleUpload]);

  return (
    <div className="rich-text-editor">
      <Toolbar editor={editor} onImageUpload={handleImageButtonClick} isUploading={isUploading} />
      {uploadError && (
        <div className="rich-text-editor__error alert alert-danger">
          {uploadError}
        </div>
      )}
      <div className="rich-text-editor__content">
        {isUploading && (
          <div className="rich-text-editor__upload-placeholder">
            <span className="spinner-border spinner-border-sm me-2" />
            {t.uploading_image}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileChange}
      />
    </div>
  );
}
